import {
  Typography,
  Card,
  StackedList,
  BannerWrapper,
  ActionModal,
  DialogPosition,
} from '@ecdlink/ui';
import { Dialog } from '@headlessui/react';
import React, { useCallback } from 'react';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useSelector } from 'react-redux';
import {
  getChildName,
  sumIncomeOrExpenseItems,
  numberWithSpaces,
  formatCurrency,
} from '@/utils/statements/statements-utils';
import { getMonthName } from '@/utils/classroom/attendance/track-attendance-utils';
import { childrenSelectors } from '@/store/children';
import { IncomeDetailsListProps } from './income-details-list.types';
import { format } from 'date-fns';
import { useDialog } from '@ecdlink/core';
import { useHistory } from 'react-router';
import ROUTES from '@/routes/routes';
import {
  statementsSelectors,
  statementsThunkActions,
} from '@/store/statements';
import { useAppDispatch } from '@/store';

export const IncomeDetailsList: React.FC<IncomeDetailsListProps> = ({
  hideDetails,
  statementTitle,
  incomeItems,
  statementMonth,
}) => {
  const children = useSelector(childrenSelectors.getChildren);
  const { isOnline } = useOnlineStatus();
  const dialog = useDialog();
  const history = useHistory();
  const appDispatch = useAppDispatch();
  const unSubmittedIncomeItems = useSelector(
    statementsSelectors.getUnsubmittedIncomeItems
  );

  const unsyncedDeletedIncomeItemIds = useSelector(
    statementsSelectors.getUnsyncedDeletedIncomeItemIds
  );

  // Derived from the prop rather than copied into local state. The old local copy was seeded once
  // on mount, so it could not reflect the store - a removed entry came straight back when this
  // screen was remounted. Deletions still queued for sync are filtered out as well, so an entry
  // removed offline stays hidden even if the statement is refetched before the queue is replayed.
  const incomeItemsList = React.useMemo(
    () =>
      (incomeItems || []).filter(
        (item) => !unsyncedDeletedIncomeItemIds?.includes(item?.id)
      ),
    [incomeItems, unsyncedDeletedIncomeItemIds]
  );

  const hasIncomeUnsubmittedItem = unSubmittedIncomeItems?.find(
    (item) => item?.id === incomeItemsList?.[0]?.id
  );

  const isStatementNotSubmitted = !!hasIncomeUnsubmittedItem;

  // TODO better mapping, or pass in mapped items
  const incomeListDetailsItems = incomeItemsList?.map((item) => {
    return {
      title: item?.childUserId
        ? getChildName(item?.childUserId!, children!) || 'Child not found' // Child may have been removed so we won't have the name
        : format(Date.parse(item.dateReceived || ''), 'dd/MM/yyyy'),
      titleStyle: 'text-textDark font-semibold text-base leading-snug',
      subTitleStyle:
        'text-sm font-h1 font-normal text-textMid w-9/12 overflow-clip',
      text: '1',
      onActionClick: () => {},
      classNames: 'bg-uiBg',
      subItem: `R ${numberWithSpaces(String(item?.amount!.toFixed(2)))}`,
      notRounded: true,
      childList: true,
      isEditable: isStatementNotSubmitted ? true : false,
      onRemoveActionClick: () => removeStatementDialog(item?.id),
      onEditActionClick: () => {
        history.push(ROUTES.BUSINESS_ADD_INCOME, {
          incomeItem: item,
          isEdit: true,
        });
      },
    };
  });

  const handleDelete = useCallback(
    async (incomeItemId: string) => {
      // The store now removes the item from the statement, online or offline, so there is no
      // local list to keep in step.
      await appDispatch(
        statementsThunkActions.deleteIncomeItem({ id: incomeItemId })
      );
    },
    [appDispatch]
  );

  const removeStatementDialog = (id: string) => {
    dialog({
      position: DialogPosition.Middle,
      render: (onSubmit, onCancel) => (
        <Dialog.Panel>
          <ActionModal
            icon={'InformationCircleIcon'}
            iconColor="alertMain"
            iconBorderColor="alertBg"
            importantText={`Are you sure you want to remove this transaction?`}
            detailText={
              'This transaction will be permanently removed. and will no longer reflect on your statement'
            }
            actionButtons={[
              {
                text: 'Yes, remove',
                textColour: 'white',
                colour: 'primary',
                type: 'filled',
                onClick: () => {
                  handleDelete(id);
                  onSubmit();
                  onCancel();
                },
                leadingIcon: 'ArrowLeftIcon',
              },
              {
                text: 'Close',
                textColour: 'primary',
                colour: 'primary',
                type: 'outlined',
                onClick: () => onCancel(),
                leadingIcon: 'PencilIcon',
              },
            ]}
          />
        </Dialog.Panel>
      ),
    });
  };

  return (
    <BannerWrapper
      showBackground={false}
      size="medium"
      renderBorder={true}
      title={`View ${getMonthName(Number(statementMonth))} preschool statement`}
      color={'primary'}
      onBack={hideDetails}
      displayOffline={!isOnline}
    >
      <div className="flex flex-col justify-center p-4">
        <Typography
          className="truncate"
          type="h2"
          weight="bold"
          color="textDark"
          text={statementTitle}
        />
        <Typography
          className="truncate"
          type="body"
          weight="bold"
          color="textMid"
          text={`${getMonthName(Number(statementMonth))} income`}
        />
        {incomeListDetailsItems && (
          <StackedList
            className="mt-4 flex w-full flex-col"
            type="MenuList"
            listItems={incomeListDetailsItems}
          />
        )}
        <Card
          className="bg-secondary flex items-center justify-between p-4"
          shadowSize={'md'}
        >
          <Typography
            text={'Total income'}
            type="body"
            color={'white'}
            className="w-8/12"
          />
          <Typography
            text={`R ${formatCurrency(
              sumIncomeOrExpenseItems(incomeItemsList)
            )}`}
            color={'white'}
            type="h4"
            className="mr-12 w-4/12 text-right"
          />
        </Card>
      </div>
    </BannerWrapper>
  );
};
